import { useEffect, useState } from "react"
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import SaveIcon from "@mui/icons-material/Save"

const FIELD_TYPE_OPTIONS = [
    { value: "text",     label: "Texto" },
    { value: "textarea", label: "Texto longo" },
    { value: "number",   label: "Número" },
    { value: "select",   label: "Lista de opções" },
    { value: "date",     label: "Data" },
    { value: "checkbox", label: "Checkbox" },
]

const initialFieldState = {
    name: "",
    field_type: "text",
    required: false,
    show_in_summary: false,
    options: [],
    placeholder: "",
}

const normalizeFieldState = (field) => ({
    ...initialFieldState,
    ...field,
    name: field?.name || "",
    field_type: field?.field_type || "text",
    required: Boolean(field?.required),
    show_in_summary: Boolean(field?.show_in_summary),
    options: (field?.options || []).map(option => typeof option === "object" ? option.value : option),
    placeholder: field?.placeholder || "",
})

const validateOptions = (options) => {
    const valid = (options || []).filter(o => o && o.trim() !== "")
    if (valid.length === 0) {
        return { isValid: false, error: "Adicione pelo menos uma opção válida." }
    }
    const lower = valid.map(o => o.trim().toLowerCase())
    if (new Set(lower).size !== lower.length) {
        return { isValid: false, error: "Opções duplicadas não são permitidas. Remova as opções repetidas." }
    }
    return { isValid: true, error: null }
}

const NewCustomFieldForm = ({ initialField, onBack, onSave, onSelectOpenChange }) => {
    const [field, setField] = useState(() => normalizeFieldState(initialField))
    const [validationError, setValidationError] = useState(null)
    const isEditing = Boolean(initialField?.id)

    useEffect(() => {
        setField(normalizeFieldState(initialField))
        setValidationError(null)
    }, [initialField])

    const update = (patch) => setField(prev => ({ ...prev, ...patch }))

    const handleBack = () => {
        setField(normalizeFieldState(initialField))
        setValidationError(null)
        onBack()
    }

    const handleSave = async () => {
        if (field.field_type === "select") {
            const validation = validateOptions(field.options)
            if (!validation.isValid) {
                setValidationError(validation.error)
                return
            }
        }
        await onSave({
            name: field.name.trim(),
            field_type: field.field_type,
            required: field.required,
            show_in_summary: field.show_in_summary,
            options: field.options.filter(o => o.trim() !== ""),
            placeholder: field.placeholder,
        })
        setField(initialFieldState)
        setValidationError(null)
    }

    const isSaveDisabled =
        !field.name.trim() ||
        (field.field_type === "select" && (
            field.options.filter(o => o.trim() !== "").length === 0 ||
            validationError !== null
        ))

    return (
        <Box sx={{ padding: "1rem", minWidth: "350px" }}>
            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
                <IconButton size="small" onClick={handleBack}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    {isEditing ? "Editar Campo Personalizado" : "Novo Campo Personalizado"}
                </Typography>
            </Box>

            <TextField
                size="small"
                placeholder="Nome do campo"
                fullWidth
                value={field.name}
                onChange={(e) => update({ name: e.target.value })}
                inputProps={{ maxLength: 64 }}
                sx={{ marginBottom: "1rem" }}
            />

            {["text", "textarea", "number"].includes(field.field_type) && (
                <TextField
                    size="small"
                    placeholder="Texto de ajuda (opcional)"
                    fullWidth
                    value={field.placeholder}
                    onChange={(e) => update({ placeholder: e.target.value })}
                    inputProps={{ maxLength: 128 }}
                    sx={{ marginBottom: "1rem" }}
                />
            )}

            <FormControl size="small" fullWidth sx={{ marginBottom: "1rem" }}>
                <InputLabel>Tipo do campo</InputLabel>
                <Select
                    value={field.field_type}
                    label="Tipo do campo"
                    onChange={(e) => {
                        update({ field_type: e.target.value, options: [] })
                        setValidationError(null)
                    }}
                    onOpen={() => onSelectOpenChange?.(true)}
                    onClose={() => onSelectOpenChange?.(false)}
                    MenuProps={{ PaperProps: { className: "paper-select-dropdown" }, style: { zIndex: 10001 } }}
                >
                    {FIELD_TYPE_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {field.field_type === "select" && (
                <Box sx={{ marginBottom: "1rem" }}>
                    <Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
                        Opções (uma por linha):
                    </Typography>
                    <TextField
                        size="small"
                        multiline
                        rows={3}
                        fullWidth
                        placeholder={"Opção 1\nOpção 2\nOpção 3"}
                        value={field.options.join("\n")}
                        onChange={(e) => {
                            const newOptions = e.target.value.split("\n")
                            update({ options: newOptions })
                            setValidationError(validateOptions(newOptions).error)
                        }}
                        error={!!validationError}
                        helperText={validationError}
                    />
                </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
                <Checkbox
                    checked={field.required}
                    onChange={(e) => update({ required: e.target.checked })}
                />
                <Typography>Campo obrigatório</Typography>
                <Checkbox
                    checked={field.show_in_summary}
                    onChange={(e) => update({ show_in_summary: e.target.checked })}
                />
                <Typography>Mostrar no resumo</Typography>
            </Box>

            <Button
                className="opportunity-btn"
                onClick={handleSave}
                disabled={isSaveDisabled}
                fullWidth
            >
                <SaveIcon />
                {isEditing ? "Salvar campo" : "Adicionar campo"}
            </Button>
        </Box>
    )
}

export default NewCustomFieldForm
